import { useState, useRef, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

interface EmailCaptureProps {
  placeholder?: string
  buttonText?: string
  source: string
  type: 'lead' | 'subscriber' | 'waitlist'
  dark?: boolean
  onSuccess?: (data: { success: boolean; message: string; downloadUrl?: string }) => void
}

function loadRecaptchaScript(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve()
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve()

  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="recaptcha"]')) {
      const check = setInterval(() => {
        if (window.grecaptcha) { clearInterval(check); resolve() }
      }, 100)
      return
    }
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    script.onload = () => {
      const check = setInterval(() => {
        if (window.grecaptcha) { clearInterval(check); resolve() }
      }, 100)
    }
    script.onerror = reject
    document.head.appendChild(script)
  })
}

async function getRecaptchaToken(): Promise<string | null> {
  if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return null
  try {
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'lead_capture' })
  } catch {
    return null
  }
}

export default function EmailCapture({
  placeholder = 'Your email address',
  buttonText = 'Send It',
  source,
  type,
  dark = false,
  onSuccess,
}: EmailCaptureProps) {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const honeypotRef = useRef<HTMLInputElement>(null)
  const recaptchaLoaded = useRef(false)

  const ensureRecaptcha = useCallback(() => {
    if (!recaptchaLoaded.current && RECAPTCHA_SITE_KEY) {
      recaptchaLoaded.current = true
      loadRecaptchaScript()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) return

    // Honeypot check — bots fill hidden fields
    if (honeypotRef.current?.value) return

    setSubmitting(true)
    try {
      const recaptchaToken = await getRecaptchaToken()

      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          type,
          ...(recaptchaToken && { recaptchaToken }),
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Something went wrong')
      }

      const data = await response.json()

      if (onSuccess) {
        onSuccess(data)
      } else {
        toast.success('Check your inbox!')
      }
      setSubmitted(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div
        className={clsx(
          'py-3.5 px-5 rounded-xl text-sm font-medium text-center',
          dark
            ? 'bg-white/10 border border-white/20 text-white'
            : 'bg-success/10 border border-success text-success'
        )}
      >
        ✓ Check your inbox! Your download is on the way.
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 max-w-[440px] w-full">
      {/* Honeypot — hidden from humans, bots fill it */}
      <input
        ref={honeypotRef}
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={ensureRecaptcha}
        placeholder={placeholder}
        required
        className={clsx(
          'flex-1 py-3 px-4 rounded-[10px] text-[15px] font-body outline-none transition-colors',
          dark
            ? 'bg-white/10 border border-white/25 text-white placeholder:text-white/50'
            : 'bg-white border border-[#d4cce5] text-text-dark placeholder:text-text-gray/60'
        )}
      />
      <button
        type="submit"
        disabled={submitting}
        className="py-3 px-5 rounded-[10px] border-none bg-gold text-text-dark text-[15px] font-bold font-body cursor-pointer whitespace-nowrap transition-all duration-150 hover:scale-[1.04] hover:shadow-[0_4px_20px_rgba(212,168,67,0.35)] disabled:opacity-50"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          buttonText
        )}
      </button>
    </form>
  )
}
